import {
  leadAttributeOptionsSelector,
  leadSourceOptionsSelector, leadStagesSelector, userMapSelector,
} from '@repo/config-services';
import { nonNullable, translate, TranslationEntity } from '@repo/utils';
import { type ComparatorType, type DataTypeType } from 'advanced-filters/data';
import { ALL_OPERATORS_CONFIG } from 'advanced-filters/data/comparatorConfig';
import { type KeyValueType, type TimeFilterOptionsType } from 'advanced-filters/store/filter';
import { deleteFilter, editFilter } from 'advanced-filters/store/filter';
import { getTimeString, getValueString } from 'advanced-filters/utils/filterContentBuilderUtils';
import {
  Button, Flex, Tag, Typography,
} from 'antd';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { FilterListItemContentProps } from '../../types/filter-list';
import FilterListItemTextContent from '../FilterListItemTextContent';
import styles from './FilterListItemContent.module.scss';

function TimeUI({
  comparator,
  timeFilter,
  className,
}: {
  comparator: ComparatorType;
  timeFilter: TimeFilterOptionsType;
  className?: string;
}) {
  return (
    <Tag bordered className={className}>
      {getTimeString({ comparator, timeFilter })}
    </Tag>
  );
}

function ValueUI({
  dataType,
  conditionValue,
  comparator,
}: {
  dataType: DataTypeType;
  conditionValue: KeyValueType;
  comparator: ComparatorType;
}) {
  const userMap = useSelector(userMapSelector);
  const leadStagesOptions = useSelector(leadStagesSelector);
  const leadSourceOptions = useSelector(leadSourceOptionsSelector);
  const leadAttributeOptions = useSelector(leadAttributeOptionsSelector);

  const content = getValueString(
    dataType,
    conditionValue,
    comparator,
    userMap,
    leadStagesOptions,
    leadSourceOptions,
    leadAttributeOptions,
  );

  if (Array.isArray(content)) {
    return content.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={index}>
        {index > 0 && (
          <Typography.Text type="secondary" className={styles.text}> OR </Typography.Text>
        )}
        <Tag bordered>{item}</Tag>
      </React.Fragment>
    ));
  }

  return <Tag bordered className={styles.valueTag}>{content}</Tag>;
}

export function FilterListItemReactContent({
  filter,
  index,
  canEdit = true,
}: Omit<FilterListItemContentProps, 'renderType'>) {
  const dispatch = useDispatch();

  const {
    conditionKey,
    conditionComparator,
    conditionValue,
    subConditionKey,
    subConditionComparator,
    subConditionValue,
    timeFilter,
  } = filter;

  const validIndex = Number.isInteger(index) && nonNullable(index);

  const handleDelete = useCallback(() => {
    if (validIndex) {
      dispatch(deleteFilter(index));
    }
  }, [dispatch, index, validIndex]);

  const handleEdit = useCallback(() => {
    if (validIndex) {
      dispatch(editFilter(index));
    }
  }, [dispatch, index, validIndex]);

  return (
    <div className={styles.filterCardContent}>
      {validIndex && <div className={styles.numberCount}>{`${index + 1}.`}</div>}
      <div className={styles.stringContent}>
        {conditionKey && (
          <>
            <Tag bordered>{conditionKey.label}</Tag>
            <Typography.Text type="secondary" className={styles.text}>
              {translate(`of the #{${TranslationEntity.lead}}`, [{
                capitalize: true,
              }])}
            </Typography.Text>
          </>
        )}

        {conditionComparator && (
          <Typography.Text type="secondary" className={styles.text}>
            {ALL_OPERATORS_CONFIG[conditionComparator].label}
          </Typography.Text>
        )}

        {conditionValue && (
          <ValueUI
            conditionValue={conditionValue}
            dataType={conditionKey.dataType}
            comparator={conditionComparator}
          />
        )}

        {subConditionKey && (
          <>
            <span className={styles.text}>, </span>
            <Tag bordered>
              {subConditionKey.label || subConditionKey.value || ''}
            </Tag>
          </>
        )}

        {subConditionComparator && (
          <Typography.Text type="secondary" className={styles.text}>
            {ALL_OPERATORS_CONFIG[subConditionComparator].label}
          </Typography.Text>
        )}

        {subConditionValue && subConditionComparator && (
          <ValueUI
            conditionValue={subConditionValue}
            dataType={subConditionKey?.dataType}
            comparator={subConditionComparator}
          />
        )}

        {timeFilter && (
          <TimeUI
            comparator={conditionComparator}
            timeFilter={timeFilter}
            className={styles.timeFilterText}
          />
        )}
      </div>

      {validIndex && canEdit && (
        <Flex className={styles.actionBtn} gap={8}>
          <Button
            type="default"
            size="small"
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            type="default"
            size="small"
            onClick={handleDelete}
          >
            Remove
          </Button>
        </Flex>
      )}
    </div>
  );
}

export default function FilterListItemContent({
  filter,
  index,
  renderType = 'react',
  canEdit = true,
}: FilterListItemContentProps) {
  if (renderType === 'text') {
    return <FilterListItemTextContent filter={filter} index={index} canEdit={canEdit} />;
  }

  return <FilterListItemReactContent filter={filter} index={index} canEdit={canEdit} />;
}
